import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { BlockFoot, type BlockTrialSlot } from '@/components/admin/sessions/walk/walk-block-parts';
import { WalkNoyauTrial } from '@/components/admin/sessions/walk/walk-noyau-trial';
import { WalkRangementTrial } from '@/components/admin/sessions/walk/walk-rangement-trial';
import { VitrineTrialsNote } from '@/components/admin/vitrine/vitrine-trials-note';
import { determinationBlockDrawn } from '@/lib/session-noyau';
import { rangementBlockDrawn } from '@/lib/session-rangement';
import { TOC_ANCHORS } from '@/lib/session-toc';
import type { SessionTocTrials } from '@/lib/session-toc';
import type { SessionDetail } from '@matter_extractor/shared';
import type { ReactNode } from 'react';

/**
 * Where the two dry trials of a session are mounted (noyau.md §9.4, scenario-succes.md §7.3 ;
 * refonte-modulaire.md §12.2 « Depuis #224 ») - decided in ONE place, for the route.
 *
 * ## In the foot of their block
 *
 * « Lecture à blanc » (the analyst) is the tool of the block « Détermination du noyau »: the
 * selector « Source » and « Relancer l'analyste à blanc » stand in its foot, and the trial it
 * starts is read under the foot, inside the block. « Rangement à blanc » (the ranger) is the same
 * for the block « Rangement de la matière ». The walk draws the blocks and calls the slots below:
 * the trials read and write through the query client, which `SessionWalk` alone does not need
 * (`sessions-parcours.test.tsx` mounts it without one).
 *
 * ## A session WITHOUT the block keeps a section
 *
 * A session that played a protocol but has no determination - a protocol with no noyau block, one
 * closed before the analyst ran - or no rangement block - a protocol with no objective target:
 * the blank rangement is exactly what turns such a protocol into a session with a column of the
 * ranger - has no foot to carry the trial. The group « Essais à blanc sur cette session » of #220
 * stays for those, under its title, and holds only the trials whose block is missing.
 *
 * ## The vitrine says it ONCE
 *
 * It makes no paid call: the note that sends the operator to form.dev (`VitrineTrialsNote`) takes
 * the place of the buttons in the foot of the first block the session has - the rangement's, then
 * the determination's - and the other foot says its origin alone. Two notes would have answered to
 * one mark twice. A session with neither block keeps the note in the group, as before.
 */

export interface SessionTrialsInput {
  readonly detail: SessionDetail;
  /** The signed-in operator, the scope of the review's cache key. */
  readonly email: string | null;
  readonly vitrine: boolean;
  /** The pill « Lu à blanc » of the head led to the trial of the analyst (#218): ring it a moment. */
  readonly ringNoyauTrial: boolean;
}

export interface SessionTrials {
  readonly noyauTrial: BlockTrialSlot | undefined;
  readonly rangementTrial: BlockTrialSlot | undefined;
  /** The group « Essais à blanc sur cette session » - null when every trial stands in its block. */
  readonly group: ReactNode;
  /** Which sections of the group are mounted, for the sommaire. */
  readonly toc: SessionTocTrials;
}

const NONE: SessionTocTrials = { noyau: false, rangement: false };

/** The vitrine's note, in the place of the buttons of a foot. */
const vitrineFoot: BlockTrialSlot = ({ origin }) => (
  <BlockFoot origin={origin} carries="note">
    <VitrineTrialsNote />
  </BlockFoot>
);

export function sessionTrials({
  detail,
  email,
  vitrine,
  ringNoyauTrial,
}: SessionTrialsInput): SessionTrials {
  const protocol = detail.cascade.protocol;
  if (protocol === null) {
    return { noyauTrial: undefined, rangementTrial: undefined, group: null, toc: NONE };
  }
  const hasNoyau = determinationBlockDrawn(detail);
  const hasRangement = rangementBlockDrawn(detail);

  if (vitrine) {
    return {
      noyauTrial: hasNoyau && !hasRangement ? vitrineFoot : undefined,
      rangementTrial: hasRangement ? vitrineFoot : undefined,
      group: hasNoyau || hasRangement ? null : <VitrineTrialsNote />,
      toc: NONE,
    };
  }

  const missing: SessionTocTrials = { noyau: !hasNoyau, rangement: !hasRangement };
  return {
    noyauTrial: hasNoyau
      ? (foot) => (
          <WalkNoyauTrial
            sessionId={detail.session.id}
            protocolId={protocol.id}
            email={email}
            flash={ringNoyauTrial}
            block={foot}
          />
        )
      : undefined,
    rangementTrial: hasRangement
      ? (foot) => <WalkRangementTrial detail={detail} protocolId={protocol.id} block={foot} />
      : undefined,
    group:
      missing.noyau || missing.rangement ? (
        <section
          data-testid={SESSIONS_TEST_IDS.walkTrials}
          aria-labelledby="sessions-essais-a-blanc-titre"
          className="flex flex-col gap-3"
        >
          <h2
            id="sessions-essais-a-blanc-titre"
            className="font-semibold text-gray-600 text-micro uppercase tracking-wide"
          >
            Essais à blanc sur cette session
          </h2>
          <p className="max-w-[80ch] text-caption text-gray-600">
            Cette session n’a pas le bloc dont l’essai est l’outil : on rejoue l’analyste ou le
            rangeur sur la matière de cette session, avec la version de développement du protocole.
            Rien de ce que le parcours ci-dessus décrit ne bouge.
          </p>
          {missing.noyau ? (
            <>
              <h3 id={TOC_ANCHORS.noyauTrial} className="font-semibold text-gray-700 text-small">
                Lecture à blanc
              </h3>
              <WalkNoyauTrial
                sessionId={detail.session.id}
                protocolId={protocol.id}
                email={email}
                flash={ringNoyauTrial}
              />
            </>
          ) : null}
          {/*
            The same for the ranger (scenario-succes.md §7.3, #177): its consigne is the
            development version's Rangement section, and the column it fills is drawn under its
            own block.
          */}
          {missing.rangement ? (
            <>
              <h3
                id={TOC_ANCHORS.rangementTrial}
                className="font-semibold text-gray-700 text-small"
              >
                Rangement à blanc
              </h3>
              <WalkRangementTrial detail={detail} protocolId={protocol.id} />
            </>
          ) : null}
        </section>
      ) : null,
    toc: missing,
  };
}
